import { Button, Card, Stack } from "react-bootstrap"
import { Link } from "react-router-dom"
import timeService from "../../../services/time/timeService"
import { PropertyBookingEntry } from "../../../Types/bookingTypes"
import { currencyFormatter } from "../../../Helpers/Parsers/currencyFormatter"
import AccessLinkButton from "../../Buttons/AccessLinkButton"
import CopyPhone from "../../Phone/CopyPhone"
import './BookingEntry.scss'
import { airBnbReservationDetailsLink } from "../../../Helpers/Booking/airbnbReservationDetails"
import { BoxArrowUpRight } from "react-bootstrap-icons"
import canAccess from "../../../services/accessService/accessService"
import PropertyTitleText from "../../PropertyEntry/PropertyTitleText"
import UserNameText from "../../Users/UserNameText"
import { vrboReservationDetailsLink } from "../../../Helpers/Booking/vrboReservationDetails"

interface BookingListItemProps {
  propertyName: string,
  bookingInfo: PropertyBookingEntry
  options?: {
    header?: string,
    disableLinks?: {
      [k: string]: boolean
    },
    details?: boolean
  },
  children?: any
  extraButtons?: any[]
}

interface BookingEntryOptionTypes {
  header: string,
  disableLinks: any,
  details: boolean
}

const defaultOptions: BookingEntryOptionTypes = {
  header: '',
  disableLinks: {
    delete: true,
  },
  details: false,
}

export const BookingEntry = (props: BookingListItemProps) => {
  const {
    bookingInfo,
    // propertyName,
    options,
    extraButtons,
    children
  } = props

  const {
    _id,
    createdBy,
    checkIn,
    checkOut,
    earnings,
    source,
    cleaning,
    pricePerNight,
    sourceConfirmation,
    bookingStatus,
    confirmationCode,
    contactInfo,
    bookingGuestInfoId,
    bookingGuestInfo,
    requiresBookingGuestInfo,
    dateBooked
  } = bookingInfo

  const {
    header,
    disableLinks,
    details
  } = {
    ...defaultOptions,
    ...options
  }

  const nights = timeService.calculateNights(checkIn, checkOut)
  
  return (
    <Card className="booking-list-item mb-3">
      {header && (
        <Card.Header>
          {header}
        </Card.Header>
      )}
      <Card.Body>
        <Card.Title>
          {contactInfo && contactInfo.firstName} {contactInfo && contactInfo.lastName}
          {contactInfo && contactInfo.phone && (
            <CopyPhone phoneNumber={contactInfo.phone} marginLeft="5px" />
          )}
        </Card.Title>
        <Card.Subtitle className="mb-2 text-muted">
          <PropertyTitleText propertyId={bookingInfo.propertyId} shortName={true} />

        </Card.Subtitle>
        <Card.Text className="small">
          <b>Check In:</b> {timeService.getFormattedTime(checkIn)} <br />
          <b>Check Out:</b> {timeService.getFormattedTime(checkOut)} <br />
          <b>Source:</b> {source}<BookingSourceIcon bookingInfo={bookingInfo} />
          <br />
          <b>Source Confirmation:</b> {sourceConfirmation} <br />
          {confirmationCode && (
            <>
              <b>Confirmation Code:</b> {confirmationCode} <br />
            </>
          )}

          {bookingGuestInfoId && (
            <>
              <b>Guest Info:</b> <Link to={`/booking-guest/${bookingGuestInfoId}`}>{bookingGuestInfoId}</Link> <br />
            </>
          )}
          {bookingGuestInfo && (
            <>
              {/* Show the main guest name and the other guest names */}
              <b>Main Guest:</b> {bookingGuestInfo.main?.firstName} {bookingGuestInfo.main?.lastName}
              {bookingGuestInfo.main?.phone && (
                <CopyPhone phoneNumber={bookingGuestInfo.main.phone} />
              )}
              <br />
              {bookingGuestInfo.guests && bookingGuestInfo.guests.length > 0 && (
                <>
                  <b>Other Guests:</b>
                  <ul className="mb-1">
                    {bookingGuestInfo.guests.map((guest, index) => (
                      <li key={index}>{guest.firstName} {guest.lastName}</li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}

          {requiresBookingGuestInfo && !bookingGuestInfoId && (
            <>
              <b>Guest Info:</b> NEEDS INFO <br />
            </>
          )}
          <b>Nights</b> {nights} <br />

          <b>Price Per Night:</b> {pricePerNight ? `${currencyFormatter(pricePerNight)}` : ''} <br />

          <b>Earnings:</b> {earnings ? `${currencyFormatter(earnings)}` : ''} <br />

          <b>Booking Status:</b> {bookingStatus} <br />
          {/* <b>PropertyId:</b> {propertyId} <br /> */}
          {/* <b>Booking Id:</b> {_id} <br /> */}
          {details && (
            <>
              <b>Cleaning:</b> {cleaning ? `${currencyFormatter(cleaning)}` : ''} <br />
            </>
          )}
          {details && createdBy && (
            <>
              <b>Created By:</b> <UserNameText userId={createdBy} asLink /> <br />
            </>
          )}
          {dateBooked && (
            <>
              <b>Date Booked:</b> {timeService.getFormattedTime(dateBooked)} <br />
            </>
          )}
        </Card.Text>

        {children && (
          <div className="booking-children mb-3">
            {(children)}
          </div>
        )}
      </Card.Body>
      <Card.Footer>
        <Stack direction={'horizontal'} gap={1} className="mt-2 mb-2">
          {!(disableLinks['view']) && (
            <AccessLinkButton
              action={'booking'}
              color={'primary'}
              size={'sm'}
              text={'View'}
              to={`/booking/${_id}`}
            />
          )}
          {!(disableLinks['edit']) && (
            <AccessLinkButton
              action={'edit-booking'}
              color={'secondary'}
              size={'sm'}
              text={'Edit'}
              to={`/edit-booking/${_id}`}
              accessOptions={{
                payload: bookingInfo
              }}
            />
          )}
          {!(disableLinks['delete']) && (
            <AccessLinkButton
              action={'delete-booking'}
              color={'danger'}
              size={'sm'}
              text={'Delete'}
              to={`/delete-booking/${_id}`}
            />
          )}
        </Stack>

        {extraButtons && extraButtons.map((button, index) => (
          <Button
            key={index}
            className="me-2 mb-2"
            size={'sm'}
            variant={button.variant ? button.variant : 'secondary'}
            disabled={button.disabled}
            onClick={() => button.onClick(bookingInfo)}>
            {button.label}
          </Button>
        ))}
      </Card.Footer>
    </Card>
  )
}

export const bookingSourceIcons: {
  [k: string]: string
} = {
  airbnb: 'https://bcalserver-storage.s3.us-east-2.amazonaws.com/airbnb-100-icon-1729015965508.png',
  homeaway: 'https://bcalserver-storage.s3.us-east-2.amazonaws.com/vrbo-100-icon-1729015993417.png',
  vrbo: 'https://bcalserver-storage.s3.us-east-2.amazonaws.com/vrbo-100-icon-1729015993417.png',
}

export const BookingSourceIcon = ({
  bookingInfo,
  onlyIcon
}: {
  bookingInfo: PropertyBookingEntry,
  onlyIcon?: boolean
}) => {

  const source = bookingInfo.source
  if (!source || !bookingSourceIcons[source]) return null

  const image = bookingSourceIcons[source]

  if (onlyIcon) {
    return <img src={image} alt={source} className="booking-source-icon" />
  }
  
  let sourceLink = ''

  if (canAccess('booking', 'airbnbReservationDetails')) {
    const airbnbReservationDetails = airBnbReservationDetailsLink(bookingInfo)
    if (airbnbReservationDetails) {
      sourceLink = airbnbReservationDetails
    }
  }
  if (canAccess('booking', 'vrboReservationDetails')) {
    const vrboReservationDetails = vrboReservationDetailsLink(bookingInfo)
    if (vrboReservationDetails) {
      sourceLink = vrboReservationDetails
    }
  }

  if (!sourceLink) return null

  return (
    <>
      <img src={image} alt={source} className="booking-source-icon" />
      <a href={sourceLink} target="_blank" rel="noreferrer">
        <BoxArrowUpRight size={14} />
      </a>
    </>
  )
}